import { StatusChipColor } from "components/StatusChip";
import { JobStatus, JobTask } from "generated-graphql/graphql";

// Statuses a failed job can be re-queued from. Kept in sync with the backend
// REQUEUEABLE_FAILED_STATUSES in apps/api/src/resolvers/jobs/requeueJob.ts.
export const REQUEUEABLE_FAILED_STATUSES: JobStatus[] = [
  JobStatus.Failed,
  JobStatus.FailedMayAutoRetry,
  JobStatus.FailedNeedsHuman,
  JobStatus.FailedQualityControlCheck,
  JobStatus.FailedProvisioningEcsTask,
];

export function canRequeue(
  status: JobStatus | null | undefined,
  task: JobTask | null | undefined
): boolean {
  if (task === JobTask.SendCheck) {
    return false;
  }

  return !!status && REQUEUEABLE_FAILED_STATUSES.includes(status);
}

// Tasks verified to re-fire an external action (email / Slack) on a double-run.
// These get a heightened warning in the re-queue confirm dialog.
export const REQUEUE_WARN_TASKS: JobTask[] = [
  JobTask.SendEmail,
  JobTask.Slack,
  JobTask.EmailDigests,
  JobTask.ReviewDigest,
];

export function isRequeueWarnTask(task: JobTask | null | undefined): boolean {
  return !!task && REQUEUE_WARN_TASKS.includes(task);
}

/**
 * Maps a job status to a {@link StatusChipColor} for rendering with `StatusChip`:
 * succeeded → success, any failure → error, everything else in flight
 * (queued / dispatched / running / cancelling / …) → info.
 *
 * Keyed exhaustively to the `JobStatus` enum: adding a new status makes the
 * `never` assignment below fail to compile, forcing a deliberate color choice
 * here rather than silently falling into a prefix match.
 */
export function getJobStatusChipColor(jobStatus: JobStatus | undefined | null): StatusChipColor {
  if (jobStatus == null) return "info";
  switch (jobStatus) {
    case JobStatus.Succeeded:
      return "success";
    case JobStatus.Failed:
    case JobStatus.FailedMayAutoRetry:
    case JobStatus.FailedNeedsHuman:
    case JobStatus.FailedProvisioningEcsTask:
    case JobStatus.FailedQualityControlCheck:
      return "error";
    case JobStatus.AwaitingManualIntervention:
    case JobStatus.Cancelled:
    case JobStatus.Cancelling:
    case JobStatus.Dispatched:
    case JobStatus.Promoted:
    case JobStatus.Provisioning:
    case JobStatus.ProvisioningEcsTask:
    case JobStatus.Queued:
    case JobStatus.Running:
    case JobStatus.Sent:
    case JobStatus.ShouldCancel:
      return "info";
    default:
      // `jobStatus` narrows to `never` here, so adding a JobStatus without a case
      // above fails to compile. The conditional consumes that `never` value and,
      // at runtime, falls back to the in-flight color for a status the typed enum
      // doesn't yet know about (e.g. a newer server) — no throwing in a render path.
      return jobStatus ? "info" : "info";
  }
}
